import {
    mdiAccount,
    mdiAccountTie,
    mdiCreation,
    mdiMinusCircleOutline,
    mdiArrowRightBold,
    mdiCheckDecagram,
    mdiClipboardPulseOutline,
    mdiClipboardTextOutline,
    mdiFileDocumentOutline,
    mdiHistory,
    mdiLock,
    mdiReceipt,
    mdiShieldOffOutline,
    mdiSwapHorizontal,
    mdiFileChart,
    mdiTrendingDown,
} from '@mdi/js';
import {
    RuleActionType,
    AUTOMATION_WORKER_ICONS,
    AutomationWorker,
    AutomationWorkerId,
} from './worker-automation.types';

export interface AutomationWorkerDefinition extends Omit<AutomationWorker, 'rules'> {}

export const AUTOMATION_WORKER_DEFINITIONS: Record<AutomationWorkerId, AutomationWorkerDefinition> = {
    rejection: {
        id: 'rejection',
        name: 'Rejection Worker',
        key: 'PROVIDER_REJECTION_WORKER',
        icon: AUTOMATION_WORKER_ICONS.rejection,
        scope: 'TRANSMISSION_STATUS',
        scopeLabel: 'Pre-adjudication · transmission failures',
        desc: 'Routes pre-adjudication transmission or clearinghouse failures into the right rejection follow-up path.',
        active: true,
    },
    denial: {
        id: 'denial',
        name: 'Denial Worker',
        key: 'PROVIDER_DENIAL_WORKER',
        icon: AUTOMATION_WORKER_ICONS.denial,
        scope: 'PAYMENT',
        scopeLabel: 'Post-adjudication · ERA denial codes',
        desc: 'Routes post-adjudication ERA denial codes into configured denial remediation workflows.',
        active: true,
    },
};

export interface ActionSummaryHeader {
    icon: string;
    title: string;
}

export interface ActionOutcomePill {
    icon: string;
    label: string;
}

export const ACTION_SUMMARY_HEADERS: Record<RuleActionType, ActionSummaryHeader> = {
    CLOSE_OUT: {
        icon: mdiMinusCircleOutline,
        title: 'ENTER closes out the claim',
    },
    SWITCH_TO_PRIVATE_PAY: {
        icon: mdiSwapHorizontal,
        title: 'ENTER converts the claim to self-pay',
    },
    CREATE_RECONSIDERATION: {
        icon: mdiCreation,
        title: 'ENTER generates the full reconsideration packet',
    },
    CREATE_MESSAGE: {
        icon: mdiAccountTie,
        title: 'ENTER creates a task for humans to review',
    },
};

export const ACTION_FIRES_HELPERS: Record<RuleActionType, string> = {
    CLOSE_OUT:
        'When this rule fires, ENTER closes the claim and stops working it. In reporting, the claim is counted as a write-off.',
    SWITCH_TO_PRIVATE_PAY:
        'When this rule fires, ENTER flips the responsibility on the claim from insurance to a self-pay patient and routes it to guarantor collections — an invoice is generated and sent to the patient.',
    CREATE_RECONSIDERATION:
        'When this rule fires, ENTER automatically assembles and submits a complete reconsideration packet to the payer. No action is required — the fields below only let you tailor it.',
    CREATE_MESSAGE:
        'When this rule fires, ENTER opens a task for a person to review the claim. Use the responsibility owner below to decide who works it.',
};

/** @deprecated Use ACTION_FIRES_HELPERS */
export const ACTION_HELPERS = ACTION_FIRES_HELPERS;

export const ACTION_OUTCOME_PILLS: Record<RuleActionType, ActionOutcomePill[]> = {
    CLOSE_OUT: [
        { icon: mdiLock, label: 'Status → Closed' },
        { icon: mdiTrendingDown, label: 'Counted as write-off' },
        { icon: mdiHistory, label: 'Audit event logged' },
    ],
    SWITCH_TO_PRIVATE_PAY: [
        { icon: mdiAccount, label: 'Responsibility → Patient' },
        { icon: mdiReceipt, label: 'Invoice generated' },
        { icon: mdiArrowRightBold, label: 'Sent to guarantor collections' },
    ],
    CREATE_RECONSIDERATION: [
        { icon: mdiFileDocumentOutline, label: 'AI cover letter' },
        { icon: mdiClipboardTextOutline, label: 'Payer reconsideration form' },
        { icon: mdiShieldOffOutline, label: 'Waiver of responsibility' },
        { icon: mdiReceipt, label: 'CMS 1500' },
        { icon: mdiFileChart, label: 'EOB' },
        { icon: mdiCheckDecagram, label: 'Proof of transmission' },
        { icon: mdiClipboardPulseOutline, label: 'All medical records' },
    ],
    CREATE_MESSAGE: [],
};

export const RULE_FIELD_HELPERS = {
    closeoutDescription: 'A brief label for why the claim was closed — shown in reporting and the claim’s audit trail.',
    memo: 'Attached to the invoice sent to the patient to explain why they’re receiving it.',
    prompt: 'Prompt our AI with any specific arguments or requirements for this reconsideration — it adjusts how the cover letter is written.',
    documents:
        'Attach anything you want included on top of the packet above (e.g. medical records, prior auth letters).',
    messageOwner: 'Who is responsible for reviewing this task.',
    messageCategory: 'Select an existing category or type to create a new one.',
    assigned: 'People to assign this task to.',
    suggestedResolution: 'Dropped into the task’s first comment to guide whoever reviews it.',
} as const;
